import { Copy } from 'lucide-react';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { useToast } from './ErrorToast';
import { Button } from './ui';

/*
 * Значение с кнопкой «Скопировать» (007 T038; дизайн-система Steps: «значения с кнопкой «Скопировать»»; прототип
 * prototypes/site-onboarding.html, шаг 4 «Связь SmartPSS с сервисом»). Отдельный файл, а не ui.tsx: компонент даёт тост,
 * а `ErrorToast.tsx` сам берёт компоненты из ui.tsx.
 */

const FAILED = 'Не удалось скопировать. Значение выделено: скопируйте его сами.';

/** Выделить текст элемента: человек скопирует его сам (меню, клавиши). */
function selectText(el: HTMLElement | null): void {
  const sel = window.getSelection();
  if (!el || !sel) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**
 * Запасной путь страниц без https и старых браузеров: скрытое поле с значением и `execCommand('copy')`, как в
 * прототипе. Разметку компонента не трогает. Получилось — `true`; фокус возвращается на кнопку.
 */
function copyHidden(value: string): boolean {
  if (typeof document.execCommand !== 'function') return false;
  const back = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const ta = document.createElement('textarea');
  ta.value = value;
  ta.setAttribute('readonly', '');
  ta.setAttribute('aria-hidden', 'true');
  ta.style.position = 'fixed';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    ta.remove();
    back?.focus();
  }
}

/**
 * Значение моноширинным и кнопка «Скопировать». Копирует `navigator.clipboard.writeText` прямо в обработчике нажатия
 * (Safari пускает в буфер обмена только так) и даёт короткий тост «Скопировано» (ux.md §2). Буфера обмена нет или он
 * отказал — значение выделяется и рядом подсказка: скопировать самому. Скрытое значение (`shown` — маска пароля) при
 * отказе показывается: выделять маску бесполезно. У кнопки — `aria-label` «Скопировать: <что>»; на телефоне подпись
 * кнопки прячется, остаётся значок, цель нажатия 40 px (`Button` `md`).
 */
export function CopyValue({
  value,
  label,
  shown,
}: {
  /** Что копируется. */
  value: string;
  /** Название значения для кнопки: «Пароль», «Имя базы». */
  label: string;
  /** Что видно на экране вместо значения: маска пароля. По умолчанию — само значение. */
  shown?: ReactNode;
}) {
  const { showInfo } = useToast();
  const box = useRef<HTMLSpanElement>(null);
  /** Номер последнего отказа: 0 — отказа не было. Каждый отказ выделяет значение заново. */
  const [failed, setFailed] = useState(0);
  useLayoutEffect(() => {
    if (failed > 0) selectText(box.current);
  }, [failed]);

  const copied = () => {
    setFailed(0);
    showInfo('Скопировано', { duration: 2000 });
  };
  const fallback = () => {
    if (copyHidden(value)) copied();
    else setFailed((n) => n + 1);
  };
  const copy = () => {
    try {
      const done = navigator.clipboard?.writeText(value);
      if (done) done.then(copied, fallback);
      else fallback();
    } catch {
      fallback();
    }
  };

  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
      <span ref={box} className="min-w-0 font-mono text-[13px] [overflow-wrap:anywhere] text-ink">
        {failed > 0 ? value : (shown ?? value)}
      </span>
      <Button variant="ghost" icon={Copy} aria-label={`Скопировать: ${label}`} onClick={copy}>
        <span className="max-sm:sr-only">Скопировать</span>
      </Button>
      {failed > 0 && (
        <span role="status" className="basis-full text-[12px] text-ink-2">
          {FAILED}
        </span>
      )}
    </span>
  );
}
